import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";

export default function Preloader() {
  const [count, setCount] = useState(0);
  const [isComplete, setIsComplete] = useState(false);
  const [isExiting, setIsExiting] = useState(false);

  useEffect(() => {
    // Fast initial load
    const fastInterval = setInterval(() => {
      setCount((prev) => {
        if (prev >= 100) {
          clearInterval(fastInterval);
          setTimeout(() => setIsExiting(true), 500);
          return 100;
        }
        // Random increments for a more organic feel
        const next = prev + Math.floor(Math.random() * 5) + 2;
        return next > 100 ? 100 : next;
      });
    }, 40);

    return () => clearInterval(fastInterval);
  }, []);

  return (
    <AnimatePresence>
      {!isComplete && (
        <motion.div
          data-testid="preloader"
          className="fixed inset-0 z-[100] flex items-center justify-center overflow-hidden"
          onAnimationComplete={() => {
            if (isExiting) setIsComplete(true);
          }}
        >
          {/* Split Backgrounds */}
          <motion.div
            initial={{ y: 0 }}
            animate={{ y: isExiting ? "-100%" : 0 }}
            transition={{ duration: 0.8, ease: [0.76, 0, 0.24, 1] }}
            className="absolute inset-x-0 top-0 h-1/2 bg-[#0a0a0a] border-b border-white/5"
          />
          <motion.div
            initial={{ y: 0 }}
            animate={{ y: isExiting ? "100%" : 0 }}
            transition={{ duration: 0.8, ease: [0.76, 0, 0.24, 1] }}
            className="absolute inset-x-0 bottom-0 h-1/2 bg-[#0a0a0a] border-t border-white/5"
          />

          {/* Grid Pattern */}
          <div className="absolute inset-0 opacity-10 pointer-events-none" style={{
            backgroundImage: 'radial-gradient(circle, #e8001d 1px, transparent 1px)',
            backgroundSize: '40px 40px'
          }} />

          {/* Center Content */}
          <motion.div
            className="relative z-10 flex flex-col items-center"
            animate={{ opacity: isExiting ? 0 : 1, scale: isExiting ? 0.9 : 1 }}
            transition={{ duration: 0.4 }}
          >
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="font-heading text-[#f5f5f5] text-xl sm:text-2xl tracking-[0.6em] uppercase mb-12"
            >
              Igniting Engines
            </motion.div>

            <div className="relative">
              <motion.div
                className="font-heading text-[#e8001d] text-[12rem] sm:text-[18rem] leading-none select-none"
                style={{
                  WebkitTextStroke: '2px rgba(232,0,29,0.3)',
                  color: 'transparent',
                  textShadow: '0 0 40px rgba(232,0,29,0.2)'
                }}
              >
                {count}
              </motion.div>
              <div className="absolute inset-0 flex items-center justify-center">
                 <div className="font-heading text-[#f5f5f5] text-8xl sm:text-9xl leading-none">
                  {count}
                </div>
              </div>
            </div>

            {/* Progress Bar */}
            <div className="mt-12 w-64 h-1 bg-white/5 rounded-full overflow-hidden">
              <motion.div
                className="h-full bg-[#e8001d]"
                style={{ width: `${count}%` }}
                transition={{ type: "spring", stiffness: 100, damping: 20 }}
              />
            </div>

            <div className="mt-4 flex gap-2">
              {[...Array(3)].map((_, i) => (
                <motion.div
                  key={i}
                  className="w-2 h-2 bg-[#e8001d]"
                  animate={{ opacity: [0.2, 1, 0.2] }}
                  transition={{ duration: 1, repeat: Infinity, delay: i * 0.2 }}
                />
              ))}
            </div>
          </motion.div>

          {/* Racing Stripes - animated exit */}
          <motion.div
            className="absolute left-0 top-0 bottom-0 w-2 flex flex-col"
            animate={{ x: isExiting ? -10 : 0 }}
          >
            <div className="flex-1 bg-[#e8001d]" />
            <div className="flex-1 bg-white" />
            <div className="flex-1 bg-[#e8001d]" />
          </motion.div>
          <motion.div
            className="absolute right-0 top-0 bottom-0 w-2 flex flex-col"
            animate={{ x: isExiting ? 10 : 0 }}
          >
            <div className="flex-1 bg-[#e8001d]" />
            <div className="flex-1 bg-white" />
            <div className="flex-1 bg-[#e8001d]" />
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

